import React, {
    createContext,
    createRef,
    FC,
    MouseEvent,
    MutableRefObject,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { mergeFn, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import { useLogger } from '@direct-frontend/modules-logger';
import { isMobile } from '@direct-frontend/platform-code';

import { isInsideTriangle, Point, updateMouseHistory } from '../utils/sidebarPopupAim';
import { SidebarPopupMenuItemActions } from '../SidebarPopup/types';

/**
 * Свойства контекста для SidebarPopup
 */
type SidebarPopupStateContextProps = {
    changeStateDelay?: number;

    /**
     * Уникальный идентификатор открытого меню
     */
    openedMenuId: string;

    /**
     * Событие, вызываемое во время переключения между меню
     */
    onChangeOpenedMenuId: (id: string) => void;

    /**
     * Таймер переключения состояний
     */
    changeStateTimer: MutableRefObject<NodeJS.Timeout | undefined> | undefined;

    /**
     * История перемещения мыши по меню
     */
    mousePositionHistory: MutableRefObject<Point[] | undefined> | undefined;

    /**
     * Уникальный идентификатор для временно открытого меню
     */
    pendingMenuId: string;

    /**
     * Событие, вызываемое во время переключения между временно открытыми меню
     */
    onChangePendingMenuId: (id: string) => void;

    /**
     * Ссылка на открытый попап
     */
    openedMenuContainerRef: RefObject<HTMLDivElement>;

    /**
     * Отступ попапа от триггера
     */
    popupOffset: number;
};

/**
 * Свойства провайдера контекста для SidebarPopup
 */
interface SidebarPopupStateProviderProps {
    /**
     * Уникальный идентификатор открытого меню
     */
    initId: string;

    /**
     * Дочерние элементы
     */
    children: ReactNode;

    /**
     * Ссылка на дом ноду с layout, нужна, чтобы вычислить отступ от правого края триггера до правого края сайбара
     * для этого посчитается padding-right layout'а сайдбара и сложится с margin-right триггера
     */
    layoutRef?: React.RefObject<HTMLDivElement>;

    /**
     * Задержка, после которой открывается / закрывается пункт меню.
     * Задержка на открытии срабатывает при условии, что сейчас уже открыт другой пункт меню
     * @default 300
     */
    changeStateDelay?: number;
}

/**
 * Контекст для SidebarPopup
 */
const SidebarPopupContext = createContext<SidebarPopupStateContextProps>({
    changeStateDelay: 300,
    openedMenuId: '',
    changeStateTimer: undefined,
    onChangeOpenedMenuId: () => {},
    mousePositionHistory: undefined,
    pendingMenuId: '',
    onChangePendingMenuId: () => {},
    openedMenuContainerRef: createRef(),
    popupOffset: 0,
});

/**
 * Провайдер контекста для SidebarPopup
 */
export const SidebarPopupStateProvider: FC<SidebarPopupStateProviderProps> = function SidebarPopupProvider({
    initId,
    changeStateDelay = 300,
    layoutRef,
    children,
}) {
    const [openedMenuId, setOpenedMenuId] = useState(initId);

    const onChangeOpenedMenuId = useCallback((id: string) => {
        setOpenedMenuId(id);
    }, []);

    const changeStateTimer = useRef<ReturnType<typeof setTimeout>>();
    const mousePositionHistory = useRef<Point[]>([]);
    const openedMenuContainerRef = useRef<HTMLDivElement>(null);

    const [pendingMenuId, setPendingMenuId] = useState(initId);

    const onChangePendingMenuId = useCallback((id: string) => {
        setPendingMenuId(id);
    }, []);

    const [popupOffset, setPopupOffset] = useState(0);
    useIsomorphicLayoutEffect(() => {
        const paddingRightPropertyValue = layoutRef?.current
            ? parseInt(window.getComputedStyle(layoutRef.current).getPropertyValue('padding-right'))
            : 0;
        const offset = Number.isNaN(paddingRightPropertyValue) ? 0 : paddingRightPropertyValue;

        setPopupOffset(offset);
    }, [layoutRef]);

    const providerValue: SidebarPopupStateContextProps = useMemo(() => {
        return {
            changeStateDelay,
            openedMenuId,
            onChangeOpenedMenuId,
            changeStateTimer,
            mousePositionHistory,
            pendingMenuId,
            onChangePendingMenuId,
            openedMenuContainerRef,
            popupOffset,
        };
    }, [
        changeStateDelay,
        openedMenuId,
        onChangeOpenedMenuId,
        pendingMenuId,
        onChangePendingMenuId,
        openedMenuContainerRef,
        popupOffset,
    ]);

    return <SidebarPopupContext.Provider value={providerValue}>{children}</SidebarPopupContext.Provider>;
};

export function useSidebarOpenedItemId() {
    const { openedMenuId } = useContext(SidebarPopupContext);

    return openedMenuId;
}

/**
 * Хук использования контекста для SidebarPopup
 */
export function useSidebarPopupState(
    openTrigger: 'click' | 'hover',
    id: string,
    triggerRef: RefObject<HTMLButtonElement | undefined>,
    onClick?: VoidFunction,
) {
    const { logClick } = useLogger('SidebarPopup');

    const {
        changeStateDelay,
        openedMenuId,
        onChangeOpenedMenuId,
        changeStateTimer,
        mousePositionHistory,
        pendingMenuId,
        onChangePendingMenuId,
        openedMenuContainerRef,
        popupOffset,
    } = useContext(SidebarPopupContext);

    const isOpen = id === openedMenuId;

    const clearTimer = useCallback(() => {
        if (changeStateTimer && changeStateTimer.current) {
            clearTimeout(changeStateTimer.current);
        }
    }, [changeStateTimer]);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);

    const onTimerEnd = useCallback(
        (action: () => void) => {
            if (!changeStateDelay) {
                action();
                return;
            }

            if (changeStateTimer) {
                // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-288723
                changeStateTimer.current = setTimeout(() => {
                    action();
                }, changeStateDelay);
            }
        },
        [changeStateDelay, changeStateTimer],
    );

    const openMenu = useCallback(() => {
        logClick({ type: 'open' });
        onChangeOpenedMenuId(id);
        if (mousePositionHistory) {
            // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-288723
            mousePositionHistory.current = [];
        }
    }, [id, logClick, mousePositionHistory, onChangeOpenedMenuId]);

    /**
     * Решение из задачи DIRECT-185762: https://nda.ya.ru/t/3tY7CjKN74V6s3
     * Решается проблема с наличием задержки при скрытии предыдущего пункта меню 2-го уровня
     * и открытии следующего, когда водим мышкой по пунктам 1-го уровня
     */
    // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-312943
    const onOpenMenu = useCallback(() => {
        clearTimer();

        if (isOpen) {
            openMenu();
            return;
        }

        if (openedMenuId === '' && openTrigger === 'hover') {
            onTimerEnd(openMenu);
            return;
        }

        if (mousePositionHistory) {
            const isAimingAtMenu = isInsideTriangle(openedMenuContainerRef.current, mousePositionHistory.current);
            if (isAimingAtMenu && openedMenuId !== '') {
                onTimerEnd(openMenu);
                return;
            }
        }

        openMenu();
    }, [
        clearTimer,
        isOpen,
        openedMenuId,
        openTrigger,
        mousePositionHistory,
        openMenu,
        onTimerEnd,
        openedMenuContainerRef,
    ]);

    const closeMenu = useCallback(() => {
        logClick({ type: 'close' });
        onChangeOpenedMenuId('');
        onChangePendingMenuId('');
    }, [logClick, onChangeOpenedMenuId, onChangePendingMenuId]);

    // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-312943
    const onCloseMenu = useCallback(() => {
        clearTimer();

        if (pendingMenuId !== '') {
            onTimerEnd(closeMenu);
            return;
        }

        if (mousePositionHistory) {
            const isAimingAtMenu = isInsideTriangle(openedMenuContainerRef.current, mousePositionHistory.current);
            if (isAimingAtMenu) {
                onChangePendingMenuId(id);
                onTimerEnd(closeMenu);
                return;
            }
        }

        onTimerEnd(closeMenu);
    }, [
        clearTimer,
        pendingMenuId,
        openedMenuContainerRef,
        mousePositionHistory,
        closeMenu,
        onTimerEnd,
        onChangePendingMenuId,
        id,
    ]);

    const onMenuLeave = useCallback(() => {
        clearTimer();
        closeMenu();
    }, [clearTimer, closeMenu]);

    const onMouseMove = useCallback(
        (event: MouseEvent) => {
            if (mousePositionHistory) {
                updateMouseHistory(event, mousePositionHistory);
            }
        },
        [mousePositionHistory],
    );

    const actions: SidebarPopupMenuItemActions = useMemo(() => {
        switch (openTrigger) {
            case 'click':
                return {
                    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-312943
                    onClick: onClick ? mergeFn(onOpenMenu, onClick) : onOpenMenu,
                    onMouseLeave: isOpen && !isMobile() ? onCloseMenu : undefined,
                };
            case 'hover':
                return {
                    onClick,
                    onMouseEnter: onOpenMenu,
                    onMouseLeave: onCloseMenu,
                };
        }
    }, [isOpen, onCloseMenu, onOpenMenu, openTrigger, onClick]);

    return {
        openedMenuId,
        openedMenuContainerRef,

        actions,

        onOpenMenu,
        onCloseMenu,

        onMenuLeave,

        onMouseMove,

        popupOffset,
    };
}
